import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

// Fetch all lab requests for a patient
export const fetchLabRequests = createAsyncThunk(
  'labRequests/fetchLabRequests',
  async(patientId, thunkAPI) => {
    try {
      const res = await fetch(`/api/v1/lab_requests?patient_id=${patientId}`);
      if (!res.ok) throw new Error("Failed to fetch lab requests");
      return await res.json();
    } catch (err) {
      return thunkAPI.rejectWithValue(err.message);
    }
  }
);

export const cancelLabRequest = createAsyncThunk(
  'labRequests/cancelLabRequest',
  async(labRequestId, thunkAPI) => {
    try {
      const res = await fetch(`/api/v1/lab_requests/${labRequestId}/cancel`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' }
      });
      if (!res.ok) throw new Error("Failed to cancel lab request");
      return await res.json();
    } catch (err) {
      return thunkAPI.rejectWithValue(err.message);
    }
  }
);

// Create a new lab request
export const createLabRequest = createAsyncThunk(
  'labRequests/createLabRequest',
  async(labRequestData, thunkAPI) => {
    try {
      const res = await fetch('/api/v1/lab_requests', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ lab_request: labRequestData })
      });
      const responseData = await res.json();
       if (!res.ok) {
        return thunkAPI.rejectWithValue(
          responseData?.errors ||  
          responseData?.error ||
          responseData?.message ||
          ['Failed to create lab request']
        );
      }

      return responseData;
    } catch (err) {
      return thunkAPI.rejectWithValue([err.message]);
    }
  }
);

const initialState = {
  list: [],
  loading: false,
  submitting: false,
  error: null
}

const labRequestsSlice = createSlice({
  name: 'labRequests',
  initialState,
  reducers: {
    addLabRequest(state, action) {
    const exists = state.list.find(req => req.id === action.payload.id);
      if (!exists) state.list.unshift(action.payload);
    },
    updateLabRequest(state, action) {
      const updated = action.payload;
      state.list = state.list.map(req =>
        req.id === updated.id ? updated : req
      );
    },
    deleteLabRequest(state, action) {
      state.list = state.list.filter(req => req.id !== action.payload);
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchLabRequests.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchLabRequests.fulfilled, (state, action) => {
        state.loading = false;
        state.list = action.payload;
      })
      .addCase(fetchLabRequests.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createLabRequest.pending, (state) => {
        state.submitting = true;
        state.error = null;
      })
      .addCase(createLabRequest.fulfilled, (state, action) => {
        state.submitting = false;
        const newRequest = action.payload;
        const exists = state.list.find(req => req.id === newRequest.id);
        if (!exists) state.list.unshift(newRequest);
      })
      .addCase(createLabRequest.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.payload;
      })
      .addCase(cancelLabRequest.pending, (state) => {
        state.submitting = true;
        state.error = null;
      })
      .addCase(cancelLabRequest.fulfilled, (state, action) => {
        state.submitting = false;
        const canceledRequest = action.payload;
        state.list = state.list.map(req =>
          req.id === canceledRequest.id ? canceledRequest : req
        );
      })
      .addCase(cancelLabRequest.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.payload;
      });
  }
});

export const { addLabRequest, updateLabRequest, deleteLabRequest } = labRequestsSlice.actions;

export const selectLabRequests = state => state.labRequests.list;
export const selectLabRequestsLoading = state => state.labRequests.loading;
export const selectLabRequestsSubmitting = state => state.labRequests.submitting;
export const selectLabRequestsError = state => state.labRequests.error;

export default labRequestsSlice.reducer;
